import { BottomNavigationAction, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, List, ListItem, ListItemIcon, ListItemSecondaryAction, ListItemText, ListSubheader, SvgIconTypeMap, Switch, TextField, Typography, useMediaQuery, useTheme } from "@material-ui/core";
import { OverridableComponent } from "@material-ui/core/OverridableComponent";
import { Add, AttachMoney, Check, CheckBoxOutlineBlank, Create, Dashboard, Delete, DirectionsWalk, DriveEta, FlashOn, FormatListBulleted, Help, Hotel, Kitchen, MoneyOff, Wc, Weekend } from "@material-ui/icons";
import { useSnackbar } from "notistack";
import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";

import { Collections, MockCollections } from "Collections";
import { useAppUser } from "components/AppUserProvider";
import { FullscreenLoading } from "components/FullscreenLoading";
import { useOrg } from "components/OrgProvider";
import { useOrgUser } from "components/OrgUserProvider";
import { PageNavigation, responsiveDrawerDeviceSize } from "components/PageNavigation";
import { usePriceFiels } from "components/PriceFieldProvider";
import { usePriceFieldSuggestions } from "components/PriceFieldSuggestionsProvider";
import { persistedDb } from "FirebaseConfigs";
import { FirebaseUtils } from "firebaseUtils/FirebaseUtils";
import { useSyncedState } from "hooks/useSyncedState";
import { AnalyticsEvent } from "models/AnalyticsEvent";
import { AppUser } from "models/AppUser";
import { Grant } from "models/Grant";
import { IdMap } from "models/IdMap";
import { IndustryTypes } from "models/IndustryTypes";
import { OfferRoom } from "models/OfferRoom";
import { Org } from "models/Org";
import { getOrgUserGrants, OrgUser } from "models/OrgUser";
import { noPriceFieldNameText, PriceField } from "models/PriceField";
import { PriceFieldCategories } from "models/PriceFieldCategories";
import { EnumPresentMap } from "models/PropertyMap";
import { Rooms } from "models/Rooms";
import { textValueFrom } from "models/TextValue";
import { Paths } from "Paths";
import { ExtraInputs, PriceFieldCategorySelector } from "screens/PriceField/PriceFieldScreen";
import { WarningModal } from "screens/Users/OrgUserScreen";
import { handleEnterKey } from "Util/handleEnterKey";
import { isEmpty } from "Util/isEmpty";
import { sortAlphabetically } from "Util/sortAlphabetically";

import { formatMoney } from "./SummaryTable";
import { NoSubscriptionAlert } from "components/NoSubscriptionAlert";

export function fieldComparator<T extends object>(field: keyof T) {
    return (a: T, b: T) => {
        const fa = a[field];
        const fb = b[field];
        if (fa < fb) {
            return -1;
        }
        if (fa > fb) {
            return 1;
        }
        return 0;
    }
}

type Props = {
    offerRooms: IdMap<OfferRoom>
    setOfferRooms: (value: IdMap<OfferRoom>) => void
    offerRoomComments: { [offerRoomId: string]: string }
    offerRoomNames: { [offerRoomId: string]: string }
    setOfferRoomComments: (value: { [offerRoomId: string]: string }) => void
    setOfferRoomNames: (value: { [offerRoomId: string]: string }) => void
    customPriceFields: IdMap<PriceField>
    setCustomPriceFields: (priceFields: IdMap<PriceField>) => void
    isSaved?: boolean
    hideUnitPrices: boolean
    setHideUnitPrices: (hide: boolean) => void
    path: string
    hasChanges: boolean
    industries: EnumPresentMap<IndustryTypes.IndustryTypes>
}

type CreatePriceFieldModalProps = {
    onClose: (value: PriceField) => void
    onCancel: () => void
    orgUser: OrgUser
    appUser: AppUser
    org: Org
    initialValue?: PriceField
}

const emptyObject: EnumPresentMap<PriceFieldCategories.Types> = {}

function CreatePriceFieldModal({ onClose, onCancel, orgUser, appUser, org, initialValue }: CreatePriceFieldModalProps) {
    const { enqueueSnackbar } = useSnackbar()
    const [name, setName] = useState(initialValue?.name?.text || '');
    const [price, setPrice] = useState(!isEmpty(initialValue?.price) ? String(initialValue?.price) : '');
    const [materialPrice, setMaterialPrice] = useState(!isEmpty(initialValue?.materialPrice) ? String(initialValue?.materialPrice) : null);
    const [expectedTime, setExpectedTime] = useState(!isEmpty(initialValue?.expectedTime?.minutes) ? String(initialValue?.expectedTime?.minutes) : null);
    const [suggestNewPriceField, setSuggestNewPriceField] = useState(!!initialValue);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down(responsiveDrawerDeviceSize), { noSsr: true })

    const grants = getOrgUserGrants(orgUser)

    const [categories, setCategories] = useState(initialValue?.categories.map || emptyObject)
    const [subCategories] = useState(initialValue?.subCategories.map || emptyObject)
    const [industryTypes] = useSyncedState(initialValue?.industryTypes.map || org.industryTypes.map)

    const asNumber = Number(price);
    const materialAsNumber = Number(materialPrice);
    const timeAsNumber = Number(expectedTime);

    const canCreatePriceField = !!grants[Grant.CREATE_PRICE_FIELD]

    const missingRequiredCategories = suggestNewPriceField && !Object.values(categories).length

    const isValid = !!(name && price && !isNaN(asNumber))
        && !missingRequiredCategories
        && (isEmpty(materialAsNumber) || !isNaN(materialAsNumber))
        && (isEmpty(expectedTime) || !isNaN(timeAsNumber))

    function onComplete() {
        if (isValid) {
            const newId = initialValue?.id || persistedDb.collection(Collections.PRICE_FIELDS).doc().id
            const priceField: PriceField = {
                id: newId,
                org,
                type: null,
                name: textValueFrom(name),
                price: asNumber,
                materialPrice: isEmpty(materialPrice) ? null : materialAsNumber,
                expectedTime: isEmpty(expectedTime) ? null : {
                    minutes: timeAsNumber,
                },
                categories: { map: categories },
                subCategories: { map: subCategories },
                industryTypes: {
                    map: industryTypes,
                },
                template: null,
            }
            if (suggestNewPriceField) {
                if (missingRequiredCategories) {
                    enqueueSnackbar('Mangler kategorier', { variant: 'error' })
                    return
                }
                FirebaseUtils.createDocument<PriceField>(
                    true,
                    canCreatePriceField ? Collections.PRICE_FIELDS : Collections.PRICE_FIELD_SUGGESTIONS,
                    newId,
                    appUser,
                    orgUser,
                    canCreatePriceField ? AnalyticsEvent.CREATED_PRICE_FIELD : AnalyticsEvent.CREATED_PRICE_FIELD_SUGGESTION,
                    AnalyticsEvent.CREATED_PRICE_FIELD_SUGGESTION,
                    canCreatePriceField ? AnalyticsEvent.CREATE_PRICE_FIELD_ERROR : AnalyticsEvent.CREATE_PRICE_FIELD_SUGGESTION_ERROR,
                    enqueueSnackbar,
                    canCreatePriceField ? 'Prisfelt opprettet' : 'Forslag sendt',
                    priceField,
                    (doc) => (canCreatePriceField ? { priceFieldId: doc.id } : { sugegstionId: doc.id }),
                )
            }
            onClose(priceField)
        }
    }


    const priceRef = useRef<any>();

    const title = canCreatePriceField ? 'Lag nytt prisfelt' : "Foreslå nytt prisfelt"

    return (
        <Dialog
            open
            onClose={onCancel}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogTitle id="alert-dialog-title">{title}</DialogTitle>
            <DialogContent>
                <List>
                    <ListItem>
                        <TextField
                            type="text"
                            variant="outlined"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            placeholder="Navn"
                            autoComplete="off"
                            autoFocus={!isMobile}
                            fullWidth
                            onKeyPress={handleEnterKey(() => priceRef.current?.focus())}
                        />
                    </ListItem>
                    <ListItem>
                        <TextField
                            type="number"
                            variant="outlined"
                            value={price}
                            onChange={e => setPrice(e.target.value)}
                            placeholder="Stykkpris"
                            fullWidth
                            ref={priceRef}
                            onKeyPress={handleEnterKey(onComplete)}
                        />
                    </ListItem>
                    <ExtraInputs
                        canEdit
                        materialPrice={materialPrice}
                        setMaterialPrice={setMaterialPrice}
                        expectedTime={expectedTime}
                        setExpectedTime={setExpectedTime}
                    />
                    {initialValue ? null : (
                        <ListItem>
                            <ListItemText id="switch-suggest-new-price-field" primary={title} />
                            <ListItemSecondaryAction>
                                <Switch color="secondary" onChange={e => setSuggestNewPriceField(e.target.checked)} checked={!!suggestNewPriceField} />
                            </ListItemSecondaryAction>
                        </ListItem>
                    )}
                    {missingRequiredCategories ? (
                        <ListItem>
                            <Typography variant="subtitle2" style={{ width: '100%', }} color="error" >Du må velge minst en kategori før du kan {(canCreatePriceField ? 'lagre prisfeltet' : 'sende forslaget')}</Typography>
                        </ListItem>
                    ) : null}
                    {suggestNewPriceField ? (
                        <PriceFieldCategorySelector
                            isSmall
                            industryTypes={industryTypes}
                            canEdit
                            createNew
                            orgUser={orgUser}
                            appUser={appUser}
                            categories={categories}
                            setCategories={setCategories}
                            collection={Collections.PRICE_FIELDS}
                            errorEvent={AnalyticsEvent.UPDATE_PRICE_FIELD_ERROR}
                            updateEvent={AnalyticsEvent.UPDATED_PRICE_FIELD}
                            addCategoryEvent={AnalyticsEvent.ADDED_CATEGORY_TO_PRICE_FIELD}
                            removeCategoryEvent={AnalyticsEvent.REMOVED_CATEGORY_FROM_PRICE_FIELD}
                        />
                    ) : null}
                </List>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} > Avbryt</Button>
                <Button disabled={!isValid} onClick={onComplete} color="primary" autoFocus>{suggestNewPriceField ? (canCreatePriceField ? 'Lag prisfelt' : 'Send forslag') : 'Ok'}</Button>
            </DialogActions>
        </Dialog >
    )
}

export function offerNavigation(path: string) {
    return [
        <BottomNavigationAction key="zones" component={Link} value={path} to={path} label="Soner" icon={<Dashboard />} />,
        <BottomNavigationAction key="send" component={Link} value={path + '/send'} to={path + '/send'} label="Send" icon={<Check />} />,
    ]
}

type NewPriceFieldsListProps = {
    customPriceFields: IdMap<PriceField>
    setCustomPriceFields?: (value: IdMap<PriceField>) => void
}
export function CustomPriceFieldsList({ customPriceFields, setCustomPriceFields }: NewPriceFieldsListProps) {
    const [priceFields] = usePriceFiels()
    const [suggestions] = usePriceFieldSuggestions()
    const [orgUserDoc] = useOrgUser()
    const [appUserDoc] = useAppUser()
    const [orgDoc] = useOrg()

    const org = orgDoc?.data || null
    const appUser = appUserDoc?.data || null
    const orgUser = orgUserDoc?.data || null

    const [showCreatePricefield, setShowCreatePricefield] = useState<PriceField | null>(null)

    const customPriceFieldList = Object.values(customPriceFields).sort((a, b) => sortAlphabetically(a.name?.text, b.name?.text))

    return (
        <>
            {(showCreatePricefield && appUser && orgUser && org) ? (
                <CreatePriceFieldModal
                    orgUser={orgUser}
                    appUser={appUser}
                    initialValue={showCreatePricefield}
                    org={org}
                    onCancel={() => setShowCreatePricefield(null)}
                    onClose={() => {
                        setShowCreatePricefield(null)
                    }}
                />
            ) : null}
            {customPriceFieldList.length ? (
                <ListSubheader>Nye prisfelter</ListSubheader>
            ) : null}
            {customPriceFieldList.map(customPriceField => (
                <ListItem key={customPriceField.id} {...(priceFields?.[customPriceField.id] ? {
                    component: Link,
                    to: Paths.PRICE_FIELDS + '/' + customPriceField.id,
                } : {})}>
                    {(setCustomPriceFields && !priceFields?.[customPriceField.id] && !suggestions?.[customPriceField.id]) ? (
                        <ListItemIcon>
                            <IconButton edge="start" onClick={() => {
                                const { [customPriceField.id]: current, ...rest } = customPriceFields
                                setCustomPriceFields({
                                    ...rest
                                })
                            }}>
                                <Delete />
                            </IconButton>
                        </ListItemIcon>
                    ) : null}
                    <ListItemText primary={noPriceFieldNameText(customPriceField.name?.text)} secondary={formatMoney(customPriceField.price)} />
                    <ListItemSecondaryAction>
                        {priceFields?.[customPriceField.id] ? (
                            <Chip
                                label="Lagt til"
                                color="primary"
                            />
                        ) : (suggestions?.[customPriceField.id] ? (
                            <Chip
                                label="Forslag sendt"
                                color="secondary"
                            />
                        ) : (
                            (appUser && orgUser && org) ? (
                                <IconButton color="secondary" onClick={() => setShowCreatePricefield(customPriceField)}>
                                    <Create />
                                </IconButton>
                            ) : null
                        ))}
                    </ListItemSecondaryAction>
                </ListItem>
            ))}
        </>
    )
}

export const roomIcons: { [P in Rooms.Type]?: OverridableComponent<SvgIconTypeMap<{}, "svg">> } = {
    [Rooms.FlatEnum.ELECTRICAL_BEDROOM]: Hotel,
    [Rooms.FlatEnum.ELECTRICAL_LIVING_ROOM]: Weekend,
    [Rooms.FlatEnum.ELECTRICAL_GARAGE]: DriveEta,
    [Rooms.FlatEnum.ELECTRICAL_BATHROOM]: Wc,
    [Rooms.FlatEnum.ELECTRICAL_HALLWAY]: DirectionsWalk,
    [Rooms.FlatEnum.ELECTRICAL_FUSE_BOX]: FlashOn,
    [Rooms.FlatEnum.ELECTRICAL_KITCHEN]: Kitchen,
    [Rooms.FlatEnum.ELECTRICAL_OTHER]: CheckBoxOutlineBlank,
}

export function OfferRoomCreationScreen({ path, hasChanges, isSaved, offerRooms, setOfferRooms, offerRoomComments, setOfferRoomComments, offerRoomNames, setOfferRoomNames, customPriceFields, setCustomPriceFields, hideUnitPrices, setHideUnitPrices }: Props) {
    const offerRoomList = Object.values(offerRooms).sort(fieldComparator('id'));

    const [orgUserDoc, loadingOrgUser] = useOrgUser()
    const [appUserDoc, loadingAppUser] = useAppUser()
    const [orgDoc, loadingOrg] = useOrg()

    const org = orgDoc?.data || null
    const appUser = appUserDoc?.data || null
    const orgUser = orgUserDoc?.data || null

    const [deleteOfferRoomId, setDeleteOfferRoomId] = useState('');
    const [showCreatePricefield, setShowCreatePricefield] = useState(false);

    const loading = loadingOrgUser || loadingOrg || loadingAppUser

    const content = (loading) ? <FullscreenLoading screenId="OfferRoomCreationScreen" /> : (
        <>
            {(showCreatePricefield && orgUser && appUser && org) ? (
                <CreatePriceFieldModal
                    orgUser={orgUser}
                    appUser={appUser}
                    org={org}
                    onCancel={() => setShowCreatePricefield(false)}
                    onClose={customPriceField => {
                        setCustomPriceFields({
                            ...customPriceFields,
                            [customPriceField.id]: customPriceField,
                        })
                        setShowCreatePricefield(false)
                    }}
                />
            ) : null}
            <List dense>
                <NoSubscriptionAlert />
                <CustomPriceFieldsList customPriceFields={customPriceFields} setCustomPriceFields={setCustomPriceFields} />
                {(appUser && orgUser) ? (
                    <ListItem button onClick={() => setShowCreatePricefield(true)}>
                        <ListItemIcon>
                            <FormatListBulleted />
                        </ListItemIcon>
                        <ListItemText primary="Nytt prisfelt" />
                        <ListItemSecondaryAction>
                            <IconButton onClick={() => setShowCreatePricefield(true)}>
                                <Add />
                            </IconButton>
                        </ListItemSecondaryAction>
                    </ListItem>
                ) : null}
                <ListSubheader>Rom</ListSubheader>
                {deleteOfferRoomId ? (
                    <WarningModal
                        title="Fjern sone"
                        onCancel={() => setDeleteOfferRoomId('')}
                        onClose={() => {
                            const { [deleteOfferRoomId]: currentOfferRoom, ...otherOfferRooms } = offerRooms
                            const { [deleteOfferRoomId]: currentComment, ...otherComments } = offerRoomComments
                            const { [deleteOfferRoomId]: currentName, ...otherNames } = offerRoomNames
                            setOfferRooms(otherOfferRooms)
                            setOfferRoomNames(otherNames)
                            setOfferRoomComments(otherComments)
                            setDeleteOfferRoomId('')
                        }}
                    >
                        Er du sikker på at du vil fjerne sonen fra anbudet?
                    </WarningModal>
                ) : null}
                {offerRoomList.map(offerRoom => {
                    const roomTag = Rooms.Flat[offerRoom.room.id]?.id
                    return (
                        <ListItem divider button component={Link} key={offerRoom.id} to={path + "/sone/" + offerRoom.id}>
                            <ListItemIcon>
                                {// TODO: Why is this breaking
                                    (roomIcons[roomTag]) && React.createElement(roomIcons[roomTag] as any)}
                            </ListItemIcon>
                            <ListItemText primary={(offerRoomNames as any)[offerRoom.id] || offerRoom.room.name} />
                            <ListItemSecondaryAction>
                                <IconButton onClick={() => setDeleteOfferRoomId(offerRoom.id)}>
                                    <Delete />
                                </IconButton>
                            </ListItemSecondaryAction>
                        </ListItem>
                    )
                })}
            </List>
        </>
    )

    const usedIndustryTypes = IndustryTypes.FlatList.filter((industryType) => !!org?.industryTypes.map[industryType.id])

    return (
        <PageNavigation
            backHistoryButton
            hasUnsavedChanges={hasChanges}
            safeUnsavedNavigation={[path + '/send', ...Object.keys(offerRooms).map(id => path + '/sone/' + id)]}
            title={isSaved ? "Utkast" : "Opprett Anbud"}
            rightAppBar={<>
                <IconButton color="inherit" aria-label="vis/gjem enhetspriser" onClick={() => setHideUnitPrices(!hideUnitPrices)}>
                    {hideUnitPrices ? <MoneyOff /> : <AttachMoney />}
                </IconButton>
            </>
            }
            rightDefaultOpen={!offerRoomList.length}
            rightDrawerIcon={<Dashboard />}
            rightDrawerTitle="Soner"
            rightDrawer={
                <List>
                    {usedIndustryTypes.map((industryType) => (
                        <React.Fragment key={industryType.id}>
                            {usedIndustryTypes.length > 1 ? (
                                <ListSubheader>{industryType.name}</ListSubheader>
                            ) : null}
                            {Rooms.GroupedList[industryType.id].sort((a, b) => sortAlphabetically(a.name, b.name)).map((roomDescription) => {
                                return (
                                    <ListItem
                                        button
                                        divider
                                        key={roomDescription.id}
                                        onClick={() => {
                                            const offerRoomId = persistedDb.collection(MockCollections.OFFER_ROOMS).doc().id
                                            setOfferRooms({
                                                ...offerRooms,
                                                [offerRoomId]: {
                                                    id: offerRoomId,
                                                    room: roomDescription,
                                                    priceFields: {},
                                                }
                                            })
                                        }}
                                    >
                                        <ListItemIcon>{React.createElement(roomIcons[roomDescription.id] || Add)}</ListItemIcon>
                                        <ListItemText>{roomDescription.name}</ListItemText>
                                    </ListItem>
                                )
                            })}
                        </React.Fragment>
                    ))}
                    <ListItem component={Link} button to={Paths.OWN_HELP_MESSAGES}>
                        <ListItemIcon>
                            <Help />
                        </ListItemIcon>
                        <ListItemText primary="Be om flere soner" />
                    </ListItem>
                </List>
            }
            bottomNavigation={offerNavigation(path)}
        >
            {content}
        </PageNavigation >
    )
}
